import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ETH,
} from '../economics/ecoShared';

import type {
  RtaLinearFit,
  RtaPoint,
} from './rtaCalculations';

import {
  calculateRtaEngineering,
} from './rtaEngineeringCalculations';

export default function RtaEngineeringPanel({ project, fitPoints, fit, methodLabel }: { project: unknown; fitPoints: RtaPoint[]; fit: RtaLinearFit | null; methodLabel: string }) {
  /*
   * 自动尝试从现有项目/数据准备中读取参数。
   * 如果字段名与当前项目不一致，
   * 用户仍可直接在下面输入框手工填写。
   */

  const detected =
    useMemo(
      () => ({
        permeability:
          findNumberRecursive(
            project,
            [
              'permeability',
              'permeabilityMd',
              'reservoirPermeability',
              'matrixPermeability',
              'reservoir_permeability',
              'matrix_permeability',
            ],
          ),

        netPay:
          findNumberRecursive(
            project,
            [
              'effectiveThickness',
              'effective_thickness',
              'netPay',
              'netPayThickness',
              'net_pay_thickness',
              'reservoirThickness',
            ],
          ),

        porosity:
          findNumberRecursive(
            project,
            [
              'porosity',
              'porosityPct',
              'reservoirPorosity',
            ],
          ),

        waterSaturation:
          findNumberRecursive(
            project,
            [
              'waterSaturation',
              'waterSaturationPct',
              'water_saturation',
            ],
          ),

        lateralLength:
          findNumberRecursive(
            project,
            [
              'horizontalSectionLength',
              'horizontal_section_length',
              'horizontalLength',
              'lateralLength',
              'lateral_length',
            ],
          ),

        fractureHalfLength:
          findNumberRecursive(
            project,
            [
              'fractureHalfLength',
              'fracture_half_length',
              'halfFractureLength',
            ],
          ),

        fractureConductivity:
          findNumberRecursive(
            project,
            [
              'fractureConductivity',
              'fracture_conductivity',
              'fractureConductivityDcm',
            ],
          ),

        srv:
          findNumberRecursive(
            project,
            [
              'srv',
              'stimulatedReservoirVolume',
              'stimulated_reservoir_volume',
            ],
          ),
      }),
      [project],
    );

  const [
    permeability,
    setPermeability,
  ] = useState('');

  const [
    netPay,
    setNetPay,
  ] = useState('');

  const [
    porosity,
    setPorosity,
  ] = useState('');

  const [
    waterSaturation,
    setWaterSaturation,
  ] = useState('');

  const [
    lateralLength,
    setLateralLength,
  ] = useState('');

  const [
    fractureHalfLength,
    setFractureHalfLength,
  ] = useState('');

  const [
    fractureConductivity,
    setFractureConductivity,
  ] = useState('');

  const [
    srv,
    setSrv,
  ] = useState('');

  /*
   * 第一次检测到数据准备参数时自动填入。
   * 已经手工填写过的值不覆盖。
   */
  useEffect(() => {
    if (
      permeability === '' &&
      detected.permeability !==
        null
    ) {
      setPermeability(
        String(
          detected.permeability,
        ),
      );
    }

    if (
      netPay === '' &&
      detected.netPay !== null
    ) {
      setNetPay(
        String(
          detected.netPay,
        ),
      );
    }

    if (
      porosity === '' &&
      detected.porosity !== null
    ) {
      setPorosity(
        String(
          detected.porosity,
        ),
      );
    }

    if (
      waterSaturation === '' &&
      detected.waterSaturation !==
        null
    ) {
      setWaterSaturation(
        String(
          detected.waterSaturation,
        ),
      );
    }

    if (
      lateralLength === '' &&
      detected.lateralLength !==
        null
    ) {
      setLateralLength(
        String(
          detected.lateralLength,
        ),
      );
    }

    if (
      fractureHalfLength === '' &&
      detected.fractureHalfLength !==
        null
    ) {
      setFractureHalfLength(
        String(
          detected.fractureHalfLength,
        ),
      );
    }

    if (
      fractureConductivity === '' &&
      detected.fractureConductivity !==
        null
    ) {
      setFractureConductivity(
        String(
          detected.fractureConductivity,
        ),
      );
    }

    if (
      srv === '' &&
      detected.srv !== null
    ) {
      setSrv(
        String(
          detected.srv,
        ),
      );
    }
  }, [
    detected,
    permeability,
    netPay,
    porosity,
    waterSaturation,
    lateralLength,
    fractureHalfLength,
    fractureConductivity,
    srv,
  ]);

  const result =
    useMemo(
      () =>
        calculateRtaEngineering(
          {
            permeabilityMd:
              toNumberOrNull(
                permeability,
              ),

            netPayM:
              toNumberOrNull(
                netPay,
              ),

            porosity:
              toNumberOrNull(
                porosity,
              ),

            waterSaturation:
              toNumberOrNull(
                waterSaturation,
              ),

            lateralLengthM:
              toNumberOrNull(
                lateralLength,
              ),

            fractureHalfLengthM:
              toNumberOrNull(
                fractureHalfLength,
              ),

            fractureConductivityDcm:
              toNumberOrNull(
                fractureConductivity,
              ),

            inputSrvM3:
              toNumberOrNull(
                srv,
              ),
          },

          fitPoints,
          fit,
        ),
      [
        permeability,
        netPay,
        porosity,
        waterSaturation,
        lateralLength,
        fractureHalfLength,
        fractureConductivity,
        srv,
        fitPoints,
        fit,
      ],
    );

  return (
    <div
      style={{
        marginTop: 14,
      }}
    >
      {/* 标题 */}
      <div
        style={{
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          RTA 工程解释
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          综合线性流段、储层参数和压裂参数开展
          SRV 几何校核与裂缝导流能力评价
        </div>
      </div>

      <div style={{ marginBottom: 12, padding: '9px 12px', background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}`, fontSize: 11, color: ETH.textSec }}>
        当前解释方法：<b>{methodLabel}</b>
      </div>
      
      {/* 线性流区间 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(4, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        <StatusCard
          title="线性流起点"
          value={
            result.linearFlowStartDay !==
            null
              ? `${fmt(
                  result.linearFlowStartDay,
                  1,
                )} d`
              : '--'
          }
          description="当前RTA拟合区间起点"
        />

        <StatusCard
          title="线性流终点"
          value={
            result.linearFlowEndDay !==
            null
              ? `${fmt(
                  result.linearFlowEndDay,
                  1,
                )} d`
              : '--'
          }
          description="当前RTA拟合区间终点"
        />

        <StatusCard
          title="拟合持续时间"
          value={
            result.linearFlowDurationDay !==
            null
              ? `${fmt(
                  result.linearFlowDurationDay,
                  1,
                )} d`
              : '--'
          }
          description={`${result.fitCount} 个拟合点`}
        />

        <StatusCard
          title="当前线性拟合 R²"
          value={
            result.fitR2 !==
            null
              ? fmt(
                  result.fitR2,
                  4,
                )
              : '--'
          }
          description={`${methodLabel}，用于描述选定区间线性一致性`}
        />
      </div>

      {/* 工程参数 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          储层与压裂参数
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 10,
            color:
              ETH.textMuted,
          }}
        >
          优先读取“数据准备”模块；
          未识别的参数可在此手工补充
        </div>

        <div
          style={{
            marginTop: 14,
            display: 'grid',
            gridTemplateColumns:
              'repeat(4, minmax(0, 1fr))',
            gap: 14,
          }}
        >
          <ParamInput
            label="基质渗透率"
            value={permeability}
            unit="mD"
            onChange={
              setPermeability
            }
          />

          <ParamInput
            label="有效厚度"
            value={netPay}
            unit="m"
            onChange={
              setNetPay
            }
          />

          <ParamInput
            label="孔隙度"
            value={porosity}
            unit="%"
            onChange={
              setPorosity
            }
          />

          <ParamInput
            label="含水饱和度"
            value={
              waterSaturation
            }
            unit="%"
            onChange={
              setWaterSaturation
            }
          />

          <ParamInput
            label="水平段长度"
            value={
              lateralLength
            }
            unit="m"
            onChange={
              setLateralLength
            }
          />

          <ParamInput
            label="裂缝半长"
            value={
              fractureHalfLength
            }
            unit="m"
            onChange={
              setFractureHalfLength
            }
          />

          <ParamInput
            label="裂缝导流能力"
            value={
              fractureConductivity
            }
            unit="D·cm"
            onChange={
              setFractureConductivity
            }
          />

          <ParamInput
            label="SRV"
            value={srv}
            unit="m³"
            onChange={
              setSrv
            }
          />
        </div>
      </div>

      {/* 工程结果 */}
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gridTemplateColumns:
            'repeat(3, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        <ResultCard
          title="改造带宽度"
          value={
            result.stimulatedWidthM !==
            null
              ? `${fmt(
                  result.stimulatedWidthM,
                  2,
                )} m`
              : '--'
          }
          description="两翼裂缝几何近似：2xf"
        />

        <ResultCard
          title="几何 SRV"
          value={
            result.geometricSrvM3 !== null
              ? `${fmtScientific(
                  result.geometricSrvM3,
                )} m³`
              : '--'
          }
          description="Lh × 2xf × h"
        />

        <ResultCard
          title="无量纲裂缝导流能力 CfD"
          value={
            result.dimensionlessFractureConductivity !==
            null
              ? fmt(
                  result.dimensionlessFractureConductivity,
                  3,
                )
              : '--'
          }
          description="Fc / (k·xf)"
          strong
        />

        <ResultCard
          title="输入SRV / 几何SRV"
          value={
            result.srvRatio !==
            null
              ? fmt(
                  result.srvRatio,
                  3,
                )
              : '--'
          }
          description="用于检查参数之间的几何一致性"
        />

        <ResultCard
          title="有效含气孔隙体积"
          value={
            result.effectiveGasPoreVolumeM3 !== null
              ? `${fmtScientific(
                  result.effectiveGasPoreVolumeM3,
                )} m³`
              : '--'
          }
          description="SRV × φ × (1−Sw)"
        />

        <ResultCard
          title="拟合段累计产气"
          value={
            result.intervalGas !==
            null
              ? `${fmt(
                  result.intervalGas,
                  2,
                )} ×10⁴m³`
              : '--'
          }
          description="当前线性流拟合区间内累计产气"
        />
      </div>

      {/* 生产变化 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          线性流阶段生产特征
        </div>

        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns:
              'repeat(3, minmax(0, 1fr))',
            gap: 12,
          }}
        >
          <MiniResult
            label="区间初始日产气"
            value={
              result.startRate !==
              null
                ? `${fmt(
                    result.startRate,
                    3,
                  )} ×10⁴m³/d`
                : '--'
            }
          />

          <MiniResult
            label="区间末期日产气"
            value={
              result.endRate !==
              null
                ? `${fmt(
                    result.endRate,
                    3,
                  )} ×10⁴m³/d`
                : '--'
            }
          />

          <MiniResult
            label="区间产量降幅"
            value={
              result.rateDeclinePct !==
              null
                ? `${fmt(
                    result.rateDeclinePct,
                    2,
                  )}%`
                : '--'
            }
          />
        </div>
      </div>

      {/* 说明 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
          color:
            ETH.textMuted,
          fontSize: 11,
          lineHeight: 1.8,
        }}
      >
        <div>
          · 当前“线性流终点”取自你在上方
          RTA 中人工选择的拟合终点，
          不自动宣称该时间一定等于真实边界控制开始时间。
        </div>

        <div>
          · 几何 SRV =
          水平段长度 × 2倍裂缝半长 ×
          有效厚度，只用于工程参数一致性检查。
        </div>

        <div>
          · 当前不利用一条 RTA
          直线单独反演裂缝半长，
          避免在线性流条件下出现参数非唯一解释。
        </div>

        <div>
          · CfD 使用裂缝导流能力、
          基质渗透率和裂缝半长计算，
          后续将在敏感性分析中研究
          xf、Fc、k 对预测产能的影响。
        </div>
      </div>
    </div>
  );
}

function ParamInput({
  label,
  value,
  unit,
  onChange,
}: {
  label: string;
  value: string;
  unit: string;
  onChange:
    (value: string) =>
      void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        gap: 5,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textSec,
        }}
      >
        {label}
      </span>

      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems:
            'center',
        }}
      >
        <input
          type="number"
          value={value}
          onChange={(
            event,
          ) =>
            onChange(
              event.target
                .value,
            )
          }
          style={
            inputStyle
          }
        />

        <span
          style={{
            fontSize: 10,
            color:
              ETH.textMuted,
            whiteSpace:
              'nowrap',
          }}
        >
          {unit}
        </span>
      </div>
    </label>
  );
}

function StatusCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <div
      style={{
        padding: 12,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 17,
          fontWeight: 800,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function ResultCard({
  title,
  value,
  description,
  strong = false,
}: {
  title: string;
  value: string;
  description: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border:
          strong
            ? '1px solid #b64f35'
            : `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 7,
          fontSize:
            strong
              ? 21
              : 18,
          fontWeight: 800,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function MiniResult({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div
      style={{
        padding: 10,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 14,
          fontWeight: 700,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function toNumberOrNull(
  value: string,
): number | null {
  if (
    value.trim() === ''
  ) {
    return null;
  }

  const number =
    Number(value);

  return Number.isFinite(
    number,
  )
    ? number
    : null;
}

function normalizeKey(
  value: string,
) {
  return value
    .replace(
      /[^a-zA-Z0-9]/g,
      '',
    )
    .toLowerCase();
}

function findNumberRecursive(
  input: unknown,
  aliases: string[],
  maxDepth = 5,
  depth = 0,
): number | null {
  if (
    input === null ||
    typeof input !==
      'object' ||
    depth >
      maxDepth
  ) {
    return null;
  }

  const record =
    input as Record<
      string,
      unknown
    >;

  const normalizedAliases =
    aliases.map(
      normalizeKey,
    );

  for (
    const [
      key,
      value,
    ] of Object.entries(
      record,
    )
  ) {
    if (
      normalizedAliases.includes(
        normalizeKey(key),
      )
    ) {
      if (
        value === null ||
        value === undefined ||
        value === ''
      ) {
        continue;
      }

      if (
        value === null ||
        value === undefined ||
        (
          typeof value === 'string' &&
          value.trim() === ''
        )
      ) {
        continue;
      }

      const number =
        Number(value);

      if (
        Number.isFinite(number) &&
        number > 0
      ) {
        return number;
      }
    }
  }

  for (
    const value of Object.values(
      record,
    )
  ) {
    if (
      value !== null &&
      typeof value ===
        'object'
    ) {
      const nested =
        findNumberRecursive(
          value,
          aliases,
          maxDepth,
          depth + 1,
        );

      if (
        nested !== null
      ) {
        return nested;
      }
    }
  }

  return null;
}

function fmt(
  value: number,
  digits = 3,
) {
  if (
    !Number.isFinite(
      value,
    )
  ) {
    return '--';
  }

  return value.toFixed(
    digits,
  );
}

function fmtScientific(
  value: number,
  digits = 3,
) {
  if (!Number.isFinite(value)) {
    return '--';
  }

  if (value === 0) {
    return '0';
  }

  const exponent = Math.floor(
    Math.log10(Math.abs(value)),
  );

  const coefficient =
    value / Math.pow(10, exponent);

  return `${coefficient.toFixed(digits)} × 10${toSuperscript(exponent)}`;
}

function toSuperscript(
  value: number,
) {
  const map: Record<string, string> = {
    '-': '⁻',
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
  };

  return String(value)
    .split('')
    .map((char) => map[char] ?? char)
    .join('');
}

const inputStyle:
  React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    boxSizing:
      'border-box',
    border: `1px solid ${ETH.headerBorder}`,
    background:
      ETH.bg,
    color:
      ETH.text,
    outline:
      'none',
  };